import type { UserAPI, UserData } from '@sydle/ui-api';
import { getAPI } from '@sydle/ui-api';
import { getCampaignParameters } from '../../utils/campaign';
import { ECOMMERCE_API, LOGGED_AREA_API } from '../api';
import { isPayloadExpired } from './utils/utils';

function setUserFromExplorerUser(explorerUser) {
  let user;

  if (explorerUser?.ecommerce) {
    user = explorerUser.ecommerce.users[0]?.accessToken?.payload;

    if (user && explorerUser.ecommerce.users[0]?.accessToken) {
      user.token = explorerUser.ecommerce.users[0].accessToken.token;
    }
  } else {
    user = explorerUser?.accessToken?.payload;

    if (user && explorerUser.accessToken.token) {
      user.token = explorerUser.accessToken.token;
    }
  }

  return user;
}

export function getAuthenticatedUser(): any {
  let user: any = null;
  try {
    if (localStorage['blogUser']) {
      user = JSON.parse(localStorage['blogUser']);
    } else if (localStorage['explorer_user']) {
      user = setUserFromExplorerUser(JSON.parse(localStorage['explorer_user']));
    } else if (localStorage['explorer_user_map']) {
      user = setUserFromExplorerUser(JSON.parse(localStorage['explorer_user_map']));
    }
  } catch (e) {
    // eslint-disable-next-line no-empty
  }

  if (user) {
    if (isPayloadExpired(user.exp)) {
      logout();
      return null;
    }
    return user;
  }
  return null;
}

export function logout(): Promise<any> | undefined {
  return getAPI<UserAPI>('userAPI')
    ?.logout()
    .then((res) => {
      localStorage.removeItem('shoppingCart');
      localStorage.removeItem('enterpriseId');

      if (res.error) {
        localStorage.removeItem('explorer_user_map');
        throw res.error.data;
      }

      return res.data;
    });
}

export function getLoginConfig(storeType?: string): any {
  return ECOMMERCE_API.fetch(
    'getLoginConfig',
    { storeType },
    { forcePost: true, requestParams: { addUserTokenOnHeader: false } },
  ).then((res) => {
    if (res.error) {
      logout();
      throw res.error.data;
    }

    return res.data;
  });
}

export function createWhatsappMessage(): Promise<any> {
  return ECOMMERCE_API.fetch(
    'createWhatsappMessage',
    {},
    { forcePost: true, requestParams: { addUserTokenOnHeader: false } },
  ).then((res) => {
    if (res.error) {
      logout();
      throw res.error.data;
    }

    return res.data;
  });
}

export function retryLogin(id: string, message: string, signal?: AbortSignal): Promise<any> {
  return ECOMMERCE_API.fetch(
    'retryLogin',
    { objectId: id, message },
    { forcePost: true, requestParams: { signal: signal, addUserTokenOnHeader: false } },
  ).then((res) => {
    if (res.error) {
      throw res.error.data;
    }

    return res.data;
  });
}

export function loginWithWhatsappMessage(id: string, message: string, signal?: AbortSignal): Promise<any> | undefined {
  localStorage.removeItem('enterpriseId');

  return getAPI<UserAPI>('userAPI')
    ?.login({
      login: `_loginWithWhatsapp_${id}`,
      password: message,
      authClassName: 'crm.person',
      requestParams: {
        signal,
      },
    })
    .then((res) => {
      if (res.error) {
        logout();
        throw res.error.data;
      }

      return res.data;
    });
}

export function getUserData(checkOpenCart?: boolean, storeType?: string): Promise<any> {
  const params = getCampaignParameters();

  return LOGGED_AREA_API.fetch(
    'getCustomer',
    { checkOpenCart, storeType, params },
    { forcePost: true, requestParams: { addUserTokenOnHeader: true } },
  ).then((res) => {
    if (res.error) {
      logout();
      throw res.error.data;
    }

    const eventId = 'getCustomer';
    const responseData = res.data as { customer: { phone: string; email: string } };
    const customer = responseData.customer;
    const phone = customer.phone ? customer.phone : undefined;
    const email = customer.email ? customer.email : undefined;

    getOrCreateLead({ phone, email }, eventId);

    return responseData;
  });
}

export function sendToken(data: string, type: string): Promise<any> {
  const params = getCampaignParameters();

  return ECOMMERCE_API.fetch(
    'createCode',
    { data, type, params },
    { forcePost: true, requestParams: { addUserTokenOnHeader: false } },
  ).then((res) => {
    if (res.error) {
      logout();
      throw res.error.data;
    }

    const eventId = 'createCode';
    const responseData = res.data;
    const isEmail = data.includes('@');
    const phone = isEmail ? undefined : data;
    const email = isEmail ? data : undefined;

    getOrCreateLead({ phone, email }, eventId);

    return responseData;
  });
}

export function getOrCreateLead({ phone, email }, eventId, customer?, additionalData?) {
  const lead = {
    ...(phone && { phone }),
    ...(email && { email }),
    name: customer?.name || phone || email,
    customer,
    additionalData,
    sync: true,
  };
  window.stag('event', eventId, { lead });
}

export function getUser(): UserData | undefined {
  return getAPI<UserAPI>('userAPI')?.getUser();
}

export function getSessionId() {
  const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  return getCookie(`_sa_first_visit_${window.location.host}`);
}

export function isLoggedIn(): boolean | undefined {
  return getAPI<UserAPI>('userAPI')?.isLoggedIn();
}

export function loginCodeCheck(emailOrPhone: string, code: string): Promise<any> | undefined {
  localStorage.removeItem('enterpriseId');

  return getAPI<UserAPI>('userAPI')
    ?.login({
      login: `_codeCheckLogin_${emailOrPhone}`,
      password: code,
      authClassName: 'crm.person',
    })
    .then((res) => {
      if (res.error) {
        logout();
        throw res.error.data;
      }
      return res.data;
    });
}

export function loginWithFacebook(facebookUrl: string): void {
  localStorage.removeItem('enterpriseId');
  window.location.href = facebookUrl + encodeURIComponent(window.location.href);
}

export function loginWithGoogle(googleUrl: string): void {
  localStorage.removeItem('enterpriseId');
  window.location.href = googleUrl + encodeURIComponent(window.location.href);
}

export function renewToken(socialToken: string): any {
  localStorage.removeItem('enterpriseId');

  return getAPI<UserAPI>('userAPI')
    ?.renewToken({
      token: socialToken,
      requestParams: {
        headers: {
          'x-explorer-authentication-class': 'crm.person',
        },
      },
    })
    .then((res) => {
      if (res.error) {
        logout();
        throw res.error.data;
      }

      return res.data;
    });
}
